/*
 * Vayve watch page (sections/custom-watch-feed.liquid): full-screen vertical video feed.
 * - List: ?start handle first, then the picked products, then the collections page by page
 *   (/collections/<handle>?view=watch-data), shuffled, newest first or as sorted.
 * - Slides come from /products/<handle>?section_id=c-watch-item and are fetched ahead.
 * - No waiting: the next video downloads while this one plays, the one after loads its first
 *   frames; videos far away are unloaded so long sessions stay light.
 * - Swipe left or right (or tap the product name, or the arrow keys) slides in the product page:
 *   its main content is fetched and placed in the panel (Shopify does not allow storefront pages
 *   in an iframe), with its scripts. Swipe back, Back to videos, or the browser Back returns to
 *   the video where it stopped.
 * - First visit: swipe help, once per browser.
 */
if (!customElements.get('c-watch')) {
  const HINT_KEY = 'c-watch-hint-seen';
  const AHEAD = 3; // slides fetched ahead of the current one
  const KEEP = 4; // videos further than this are unloaded
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  const shuffle = (a) => {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  const list = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

  class CWatch extends HTMLElement {
    connectedCallback() {
      this.root = this.dataset.root || '/';
      this.feed = this.querySelector('[data-c-watch-feed]');
      this.stage = this.querySelector('[data-c-watch-stage]');
      this.panel = this.querySelector('[data-c-watch-panel]');
      this.page = this.querySelector('[data-c-watch-page]');
      this.muteBtn = this.querySelector('[data-c-watch-mute]');
      this.loading = this.querySelector('[data-c-watch-loading]');
      this.hint = this.querySelector('[data-c-watch-hint]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.muted = true;
      this.slides = [];
      this.known = new Set();
      this.cache = new Map();
      this.active = -1;
      this.side = null;
      this.pushed = false;
      this.rr = 0;

      const q = new URLSearchParams(window.location.search);
      const fromLink = q.has('p') || q.has('c');
      this.order = (fromLink && q.get('o')) || this.dataset.order || 'shuffle';
      this.limit = Math.max(1, parseInt(this.dataset.limit, 10) || 200);
      const products = list(fromLink ? q.get('p') : this.dataset.products);
      this.cols = list(fromLink ? q.get('c') : this.dataset.collections).map((h) => ({ h, page: 1, done: false }));
      if (this.order === 'shuffle') {
        shuffle(products);
        shuffle(this.cols);
      }

      document.documentElement.classList.add('c-watch-lock');
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) this.activate(this.slides.indexOf(e.target));
        });
      }, { root: this.feed, threshold: [0.6] });

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onPop = this.onPop.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKey);
      window.addEventListener('popstate', this.onPop);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.feed.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.feed.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.feed.addEventListener('touchend', this.onTouchEnd);
      this.feed.addEventListener('touchcancel', this.onTouchEnd);
      this.bindPanelSwipe();
      // Product links inside the product page panel open in the panel, so Back to videos still works.
      this.page.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target.closest && e.target.closest('a[href]');
        if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
        let u;
        try { u = new URL(a.href, window.location.href); } catch (err) { return; }
        if (u.origin !== window.location.origin || !/\/products\/[^/]+\/?$/.test(u.pathname)) return;
        if (u.pathname === window.location.pathname) return;
        e.preventDefault();
        this.setPage(u.pathname + u.search);
      });
      this.onCartChange = () => this.updateCount();
      document.addEventListener('cart:updated', this.onCartChange);
      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        this.cartUnsub = window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => window.setTimeout(this.onCartChange, 300));
      }

      const start = q.get('start');
      if (start) this.add([start]);
      this.add(products);
      this.boot();
    }

    disconnectedCallback() {
      document.documentElement.classList.remove('c-watch-lock');
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onPop);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('cart:updated', this.onCartChange);
      if (typeof this.cartUnsub === 'function') this.cartUnsub();
      if (this.observer) this.observer.disconnect();
    }

    async boot() {
      while (!this.slides.length && this.cols.some((c) => !c.done)) await this.loadPage();
      if (!this.slides.length) return this.showEmpty();
      this.activate(0);
      this.fill();
    }

    showEmpty() {
      this.feed.setAttribute('aria-busy', 'false');
      this.loading.hidden = true;
      this.querySelector('[data-c-watch-empty]').hidden = false;
    }

    // ---- list -------------------------------------------------------------------------------
    add(handles) {
      handles.forEach((h) => {
        if (this.known.has(h) || this.slides.length >= this.limit) return;
        this.known.add(h);
        const el = document.createElement('section');
        el.className = 'c-watch__slide';
        el.dataset.handle = h;
        this.feed.appendChild(el);
        this.slides.push(el);
        this.observer.observe(el);
      });
    }

    async loadPage() {
      const open = this.cols.filter((c) => !c.done);
      if (!open.length) return;
      const col = open[this.rr++ % open.length];
      let url = this.root + 'collections/' + encodeURIComponent(col.h) + '?view=watch-data&page=' + col.page;
      if (this.order === 'newest') url += '&sort_by=created-descending';
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        const data = res.ok ? JSON.parse(await res.text()) : null;
        if (!data) throw new Error('watch data');
        const hs = data.h || [];
        this.add(this.order === 'shuffle' ? shuffle(hs) : hs);
        col.page += 1;
        col.done = !data.more;
      } catch (e) {
        col.done = true;
      }
    }

    // Keeps enough slides queued after the current one.
    async fill() {
      if (this.filling) return;
      this.filling = true;
      while (this.slides.length - this.active <= AHEAD + 3 && this.slides.length < this.limit && this.cols.some((c) => !c.done)) {
        await this.loadPage();
      }
      this.filling = false;
      this.ensure();
    }

    // ---- slides -----------------------------------------------------------------------------
    get(h) {
      if (!this.cache.has(h)) {
        const url = this.root + 'products/' + encodeURIComponent(h) + '?section_id=c-watch-item';
        this.cache.set(h, fetch(url, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const item = doc.querySelector('[data-c-watch-item]');
            return item ? item.outerHTML : null;
          })
          .catch(() => null));
      }
      return this.cache.get(h);
    }

    ensure() {
      const from = Math.max(0, this.active - 1);
      const to = Math.min(this.slides.length - 1, this.active + AHEAD);
      for (let i = from; i <= to; i++) this.load(this.slides[i]);
    }

    load(slide) {
      if (!slide || slide.dataset.state) return;
      slide.dataset.state = 'loading';
      this.get(slide.dataset.handle).then((html) => {
        if (!slide.isConnected) return;
        if (!html) return this.drop(slide);
        slide.innerHTML = html;
        slide.dataset.state = 'ready';
        // Slides arrive after wishlist.js marked the page: show saved products as liked.
        let saved = [];
        try { saved = JSON.parse(window.localStorage.getItem('m-wishlist-products')) || []; } catch (e) { saved = []; }
        slide.querySelectorAll('.c-watch__heart').forEach((b) => {
          if (saved.indexOf(b.dataset.productHandle) > -1) b.classList.add('added-to-wishlist');
        });
        const v = slide.querySelector('video');
        if (v) {
          v.addEventListener('waiting', () => slide.classList.add('is-buffering'));
          v.addEventListener('playing', () => slide.classList.remove('is-buffering', 'is-paused'));
        }
        this.prime();
        if (this.slides[this.active] === slide) {
          this.loading.hidden = true;
          this.feed.setAttribute('aria-busy', 'false');
          this.maybeHint();
        }
      });
    }

    // Product without a video (or removed): take its slide out of the feed.
    drop(slide) {
      const i = this.slides.indexOf(slide);
      if (i < 0) return;
      this.observer.unobserve(slide);
      const wasActive = i === this.active;
      slide.remove();
      this.slides.splice(i, 1);
      if (i < this.active || (wasActive && i >= this.slides.length)) this.active -= 1;
      if (!this.slides.length) {
        if (this.cols.some((c) => !c.done)) {
          this.active = -1;
          this.boot();
        } else {
          this.showEmpty();
        }
        return;
      }
      if (wasActive) {
        const keep = this.active < 0 ? 0 : this.active;
        this.active = -1;
        this.activate(keep);
      }
      this.fill();
    }

    // Preload policy around the current slide.
    prime() {
      this.slides.forEach((slide, i) => {
        const v = slide.querySelector('video');
        if (!v) return;
        const d = i - this.active;
        if (d === 0 || d === 1) this.attach(v, 'auto');
        else if (d === 2 || d === -1) this.attach(v, 'metadata');
        else if (Math.abs(d) > KEEP) this.detach(v);
        if (d === 0) this.play(v);
        else if (!v.paused) v.pause();
      });
    }

    attach(v, preload) {
      if (!v.getAttribute('src')) {
        v.preload = preload;
        v.src = v.dataset.src;
      } else if (preload === 'auto' && v.preload !== 'auto') {
        v.preload = 'auto';
        if (v.readyState < 3 && v.paused) v.load();
      }
    }

    detach(v) {
      if (!v.getAttribute('src')) return;
      v.pause();
      v.removeAttribute('src');
      v.load();
    }

    play(v) {
      if (!v || this.side || v.dataset.userPaused === 'true') return;
      v.muted = this.muted;
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {
          if (!v.muted) {
            this.setMuted(true);
            v.play().catch(() => {});
          }
        });
      }
    }

    current() {
      return this.slides[this.active] || null;
    }

    activate(i) {
      if (i < 0 || i === this.active || !this.slides[i]) return;
      const prev = this.current();
      if (prev) {
        const pv = prev.querySelector('video');
        if (pv) {
          pv.pause();
          delete pv.dataset.userPaused;
          prev.classList.remove('is-paused');
        }
      }
      this.active = i;
      const slide = this.slides[i];
      this.loading.hidden = slide.dataset.state === 'ready';
      this.ensure();
      this.prime();
      if (this.slides.length - i <= AHEAD + 3) this.fill();
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('start', slide.dataset.handle);
        window.history.replaceState(window.history.state, '', u.toString());
      } catch (e) { /* ignore */ }
    }

    go(step) {
      const t = this.slides[this.active + step];
      if (t) t.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' });
    }

    setMuted(m) {
      this.muted = m;
      this.classList.toggle('is-sound', !m);
      this.muteBtn.setAttribute('aria-pressed', String(!m));
      this.muteBtn.setAttribute('aria-label', m ? 'Unmute' : 'Mute');
      const s = this.current();
      const v = s && s.querySelector('video');
      if (v) v.muted = m;
    }

    // ---- events -----------------------------------------------------------------------------
    onClick(e) {
      const t = e.target;
      if (t.closest('[data-c-watch-mute]')) return this.setMuted(!this.muted);
      const cartLink = t.closest('[data-c-watch-cart]');
      if (cartLink) {
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          e.preventDefault();
          drawer.open();
        }
        return;
      }
      if (t.closest('[data-c-watch-prev]')) return this.go(-1);
      if (t.closest('[data-c-watch-next]')) return this.go(1);
      if (t.closest('[data-c-watch-back]')) return this.closePanel();
      if (t.closest('[data-c-watch-hint-ok]') || t.closest('[data-c-watch-hint]')) return this.hideHint();
      const close = t.closest('[data-c-watch-close]');
      if (close) {
        let same = false;
        try { same = document.referrer && new URL(document.referrer).origin === window.location.origin; } catch (err) { same = false; }
        if (same && window.history.length > 1) {
          e.preventDefault();
          window.history.back();
        }
        return;
      }
      const open = t.closest('[data-c-watch-open]');
      if (open) {
        e.preventDefault();
        return this.openPanel('right');
      }
      const share = t.closest('[data-c-watch-share]');
      if (share) return this.share(share);
      const add = t.closest('[data-c-watch-add]');
      if (add) return this.addToCart(add);
      const video = t.closest('.c-watch__video');
      if (video) {
        const slide = video.closest('.c-watch__slide');
        if (video.paused) {
          delete video.dataset.userPaused;
          this.play(video);
        } else {
          video.pause();
          video.dataset.userPaused = 'true';
          slide.classList.add('is-paused');
        }
      }
    }

    onKey(e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (document.querySelector('.m-modal.m-open, .m-modal.is-open, m-cart-drawer[open], .m-cart-drawer.m-cart-drawer--active')) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (this.side) {
        if (e.key === 'Escape') this.closePanel();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); this.go(1); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); this.go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.openPanel('right'); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.openPanel('left'); }
      else if (e.key === 'm' || e.key === 'M') this.setMuted(!this.muted);
    }

    onVisibility() {
      const s = this.current();
      const v = s && s.querySelector('video');
      if (!v) return;
      if (document.hidden) v.pause();
      else this.play(v);
    }

    // Horizontal swipe on the feed drags the product page in from that side.
    onTouchStart(e) {
      if (this.side || e.touches.length !== 1) return;
      const p = e.touches[0];
      this.touch = { x: p.clientX, y: p.clientY, t: Date.now(), axis: null, dx: 0 };
      this.hideHint();
    }

    onTouchMove(e) {
      const tc = this.touch;
      if (!tc || e.touches.length !== 1) return;
      const p = e.touches[0];
      const dx = p.clientX - tc.x;
      const dy = p.clientY - tc.y;
      if (!tc.axis) {
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          tc.axis = 'x';
          this.setPage();
          this.classList.add('is-dragging');
        } else if (Math.abs(dy) > 12) {
          tc.axis = 'y';
        }
      }
      if (tc.axis !== 'x') return;
      e.preventDefault();
      tc.dx = dx;
      this.dragTo(dx);
    }

    onTouchEnd() {
      const tc = this.touch;
      this.touch = null;
      if (!tc || tc.axis !== 'x') return;
      this.classList.remove('is-dragging');
      const w = this.offsetWidth || window.innerWidth;
      const speed = Math.abs(tc.dx) / Math.max(1, Date.now() - tc.t);
      if (Math.abs(tc.dx) > w * 0.22 || (Math.abs(tc.dx) > 40 && speed > 0.45)) this.openPanel(tc.dx < 0 ? 'right' : 'left');
      else this.resetDrag();
    }

    dragTo(dx) {
      const side = dx < 0 ? 'right' : 'left';
      this.panel.hidden = false;
      this.panel.dataset.side = side;
      this.stage.style.transform = 'translate3d(' + dx + 'px,0,0)';
      this.panel.style.transform = 'translate3d(calc(' + (side === 'right' ? '100%' : '-100%') + ' + ' + dx + 'px),0,0)';
    }

    resetDrag() {
      this.stage.style.transform = '';
      this.panel.style.transform = '';
      if (!this.side) {
        window.setTimeout(() => {
          if (!this.side) this.panel.hidden = true;
        }, 320);
      }
    }

    // ---- product page panel -----------------------------------------------------------------
    // url: another product opened from inside the panel (grouped product swatch, related product).
    setPage(url) {
      if (!url) {
        const s = this.current();
        const item = s && s.querySelector('[data-c-watch-item]');
        if (!item) return false;
        url = item.dataset.url;
      }
      if (this.pageUrl === url) return true;
      this.pageUrl = url;
      const token = (this.pageToken = (this.pageToken || 0) + 1);
      this.panel.classList.add('is-loading');
      this.page.innerHTML = '';
      this.page.scrollTop = 0;
      fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error('product page'))))
        .then((html) => {
          if (token !== this.pageToken) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const main = doc.querySelector('main#MainContent') || doc.querySelector('main');
          if (!main) throw new Error('no main');
          // Variant changes must not rewrite the watch page address.
          main.querySelectorAll('[data-update-url]').forEach((el) => el.setAttribute('data-update-url', 'false'));
          // Upgrade custom elements before inserting, so a parent's connectedCallback finds its
          // children ready (e.g. product-info calling the quantity input's methods).
          const box = document.createElement('div');
          box.innerHTML = main.innerHTML;
          if (window.customElements && customElements.upgrade) customElements.upgrade(box);
          this.page.replaceChildren(...box.childNodes);
          this.runScripts(this.page);
          this.panel.classList.remove('is-loading');
        })
        .catch(() => {
          if (token !== this.pageToken) return;
          this.pageUrl = null;
          this.panel.classList.remove('is-loading');
          window.location.href = url; // fall back to the normal product page
        });
      return true;
    }

    // Scripts inserted with innerHTML do not run: re-create them. Shared files load only once.
    runScripts(root) {
      root.querySelectorAll('script').forEach((old) => {
        const type = (old.getAttribute('type') || '').toLowerCase();
        if (type && type !== 'text/javascript' && type !== 'module' && type !== 'application/javascript') return;
        const src = old.getAttribute('src');
        if (src) {
          const abs = new URL(src, window.location.href).href;
          const loaded = Array.from(document.scripts).some((x) => x !== old && x.src === abs && !root.contains(x));
          old.remove();
          if (loaded) return;
          const sc = document.createElement('script');
          sc.src = src;
          if (type === 'module') sc.type = 'module';
          sc.async = false;
          document.body.appendChild(sc);
          return;
        }
        const sc = document.createElement('script');
        if (type === 'module') sc.type = 'module';
        sc.textContent = old.textContent;
        old.replaceWith(sc);
      });
    }

    openPanel(side) {
      if (!this.setPage()) return;
      const s = this.current();
      const v = s && s.querySelector('video');
      if (v) v.pause();
      this.side = side;
      this.panel.hidden = false;
      this.panel.dataset.side = side;
      this.classList.add('is-product');
      void this.panel.offsetWidth;
      this.stage.style.transform = 'translate3d(' + (side === 'right' ? '-100%' : '100%') + ',0,0)';
      this.panel.style.transform = 'translate3d(0,0,0)';
      this.panel.setAttribute('aria-hidden', 'false');
      if (!this.pushed) {
        window.history.pushState({ cWatch: 'product' }, '', window.location.href);
        this.pushed = true;
      }
      this.hideHint();
      const back = this.panel.querySelector('[data-c-watch-back]');
      if (back) back.focus({ preventScroll: true });
    }

    closePanel() {
      if (!this.side) return;
      if (this.pushed) {
        window.history.back(); // onPop finishes closing
        return;
      }
      this.finishClose();
    }

    onPop() {
      if (!this.side) return;
      this.pushed = false;
      this.finishClose();
    }

    finishClose() {
      const side = this.side;
      this.side = null;
      this.classList.remove('is-product');
      this.stage.style.transform = '';
      this.panel.style.transform = 'translate3d(' + (side === 'left' ? '-100%' : '100%') + ',0,0)';
      this.panel.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => {
        if (!this.side) {
          this.panel.hidden = true;
          this.panel.style.transform = '';
        }
      }, 320);
      // The product page may have changed the cart; refresh the count quietly.
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      this.updateCount();
      const s = this.current();
      const v = s && s.querySelector('video');
      if (v) {
        delete v.dataset.userPaused;
        this.play(v);
      }
    }

    // On the product page, a sideways swipe toward the video closes it (sliders inside the page
    // keep their own swipes).
    bindPanelSwipe() {
      let tc = null;
      const panel = this.panel;
      panel.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        tc = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now(), axis: null, dx: 0 };
        if (e.target.closest && e.target.closest('.swiper-container, .swiper, .m-modal, [data-c-no-swipe], input[type=range], .c-pgroup__list, .m-product-option--content')) tc = null;
      }, { passive: true });
      panel.addEventListener('touchmove', (e) => {
        if (!tc || !this.side) return;
        const dx = e.touches[0].clientX - tc.x;
        const dy = e.touches[0].clientY - tc.y;
        if (!tc.axis) {
          if (Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy) * 1.4) tc.axis = 'x';
          else if (Math.abs(dy) > 12) tc.axis = 'y';
        }
        if (tc.axis !== 'x') return;
        const toward = this.side === 'right' ? dx > 0 : dx < 0;
        if (!toward) return;
        e.preventDefault();
        tc.dx = dx;
        this.classList.add('is-dragging');
        const w = this.offsetWidth || window.innerWidth;
        const base = this.side === 'right' ? -w : w;
        this.stage.style.transform = 'translate3d(' + (base + dx) + 'px,0,0)';
        panel.style.transform = 'translate3d(' + dx + 'px,0,0)';
      }, { passive: false });
      const end = () => {
        const t = tc;
        tc = null;
        if (!t || t.axis !== 'x' || !this.side) return;
        this.classList.remove('is-dragging');
        const w = this.offsetWidth || window.innerWidth;
        const speed = Math.abs(t.dx) / Math.max(1, Date.now() - t.t);
        if (Math.abs(t.dx) > w * 0.25 || (Math.abs(t.dx) > 40 && speed > 0.45)) this.closePanel();
        else {
          this.stage.style.transform = 'translate3d(' + (this.side === 'right' ? '-100%' : '100%') + ',0,0)';
          panel.style.transform = 'translate3d(0,0,0)';
        }
      };
      panel.addEventListener('touchend', end);
      panel.addEventListener('touchcancel', end);
    }

    // Share sheet on phones, else copy the product link.
    share(btn) {
      const url = btn.dataset.url;
      if (navigator.share) {
        navigator.share({ title: btn.dataset.title || document.title, url: url }).catch(() => {});
        return;
      }
      const done = () => {
        btn.classList.add('is-copied');
        window.setTimeout(() => btn.classList.remove('is-copied'), 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, () => window.prompt('Copy link', url));
      else window.prompt('Copy link', url);
    }

    // ---- add to cart ------------------------------------------------------------------------
    async addToCart(btn) {
      const id = Number(btn.getAttribute('data-variant-id'));
      if (!id || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      const item = btn.closest('[data-c-watch-item]');
      const msg = item ? item.querySelector('[data-c-watch-msg]') : null;
      if (msg) msg.textContent = '';
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('m-spinner-loading');
      if (window.CVayveCartFx && window.CVayveCartFx.early) window.CVayveCartFx.early(btn);
      try {
        const res = await fetch(this.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          if (msg) msg.textContent = data.description || data.message || 'Could not add to bag.';
          return;
        }
        // The header cart icon is covered by the watch page: confirm on the button and on the
        // watch page's own cart count. The drawer opens only when the store is set to open it.
        const ms = window.MinimogSettings || {};
        const openDrawer = !!document.querySelector('m-cart-drawer') && ms.enable_cart_drawer && (ms.c_atc_action || 'fly') !== 'fly';
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: openDrawer, source: btn } }));
        this.updateCount();
        const label = btn.textContent;
        btn.textContent = this.dataset.added || 'Added';
        btn.classList.add('is-added');
        window.setTimeout(() => {
          btn.textContent = label;
          btn.classList.remove('is-added');
        }, 2000);
      } catch (err) {
        if (msg) msg.textContent = 'Could not add to bag. Please try again.';
      } finally {
        btn.removeAttribute('aria-busy');
        btn.classList.remove('m-spinner-loading');
        btn.disabled = false;
      }
    }

    updateCount() {
      fetch(this.root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => {
          const el = this.querySelector('[data-c-watch-count]');
          if (!el) return;
          el.textContent = c.item_count;
          el.hidden = !c.item_count;
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        })
        .catch(() => {});
    }

    // ---- first-visit help -------------------------------------------------------------------
    maybeHint() {
      if (this.hintShown || this.dataset.hint !== 'true' || store.get(HINT_KEY)) return;
      this.hintShown = true;
      this.hint.hidden = false;
      this.classList.add('is-hint');
    }

    hideHint() {
      if (!this.hint || this.hint.hidden) return;
      this.hint.hidden = true;
      this.classList.remove('is-hint');
      store.set(HINT_KEY, '1');
    }
  }
  customElements.define('c-watch', CWatch);
}
